import { useEffect, useMemo, useState } from "react";

import type { LoginItemData } from "@ext/types";

import { type DecryptedItem, Session } from "./auth";
import { ChangePassword } from "./screens/ChangePassword";
import { Detail } from "./screens/Detail";
import { Editor } from "./screens/Editor";
import { List } from "./screens/List";
import { Settings } from "./screens/Settings";
import { Unlock } from "./screens/Unlock";
import { loadState, saveState } from "./storage";

type Screen =
  | { name: "unlock" }
  | { name: "list" }
  | { name: "detail"; item: DecryptedItem }
  | { name: "editor"; item: DecryptedItem | null }
  | { name: "settings" }
  | { name: "changepw" };

export function App() {
  const session = useMemo(() => new Session(), []);
  const [screen, setScreen] = useState<Screen>({ name: "unlock" });
  const [items, setItems] = useState<DecryptedItem[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [faceIdEnrolled, setFaceIdEnrolled] = useState(session.faceIdEnrolled());

  function showToast(msg: string) {
    setToast(msg);
  }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1600);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleUnlockPassword(masterPassword: string) {
    await session.unlock(masterPassword);
    setItems(session.cachedItems);
    setScreen({ name: "list" });

    // After first successful manual unlock, offer Face ID one-time.
    if (
      !session.faceIdEnrolled() &&
      !loadState().faceIdAsked &&
      (await session.faceIdSupported())
    ) {
      // Defer to next tick so the list renders first.
      setTimeout(() => {
        saveState({ faceIdAsked: true });
        if (confirm("Chcesz odblokowywać sejf Face ID-em zamiast wpisywać hasło główne?")) {
          void session
            .enableFaceId()
            .then(() => {
              setFaceIdEnrolled(true);
              showToast("Face ID włączone");
            })
            .catch((e) => showToast(`Face ID: ${(e as Error).message}`));
        }
      }, 400);
    }
  }

  async function handleUnlockFaceId() {
    await session.unlockWithFaceId();
    setItems(session.cachedItems);
    setFaceIdEnrolled(session.faceIdEnrolled()); // may have been auto-disabled
    setScreen({ name: "list" });
  }

  async function handleSync() {
    await session.refresh();
    setItems(session.cachedItems);
  }

  function handleLock() {
    void session.lock();
    setItems([]);
    setScreen({ name: "unlock" });
  }

  async function handleSave(data: LoginItemData) {
    const editing = screen.name === "editor" ? screen.item : null;
    if (editing) {
      await session.updateExisting(editing.id, data);
      showToast("Zaktualizowano");
    } else {
      await session.encryptAndUpload(data);
      showToast("Dodano wpis");
    }
    setItems(session.cachedItems);
    setScreen({ name: "list" });
  }

  async function handleDelete() {
    if (screen.name !== "editor" || !screen.item) return;
    await session.deleteExisting(screen.item.id);
    showToast("Usunięto");
    setItems(session.cachedItems);
    setScreen({ name: "list" });
  }

  return (
    <>
      {screen.name === "unlock" && (
        <Unlock
          onSubmit={handleUnlockPassword}
          onFaceId={faceIdEnrolled ? handleUnlockFaceId : undefined}
          faceIdEnrolled={faceIdEnrolled}
        />
      )}
      {screen.name === "list" && (
        <List
          items={items}
          onOpen={(item) => setScreen({ name: "detail", item })}
          onAdd={() => setScreen({ name: "editor", item: null })}
          onLock={handleLock}
          onSync={handleSync}
          onSettings={() => setScreen({ name: "settings" })}
        />
      )}
      {screen.name === "detail" && (
        <Detail
          item={screen.item}
          onBack={() => setScreen({ name: "list" })}
          onEdit={() => setScreen({ name: "editor", item: screen.item })}
          onToast={showToast}
        />
      )}
      {screen.name === "editor" && (
        <Editor
          item={screen.item}
          onSave={handleSave}
          onDelete={screen.item ? handleDelete : undefined}
          onCancel={() => setScreen(screen.item ? { name: "detail", item: screen.item } : { name: "list" })}
        />
      )}
      {screen.name === "settings" && (
        <Settings
          session={session}
          onBack={() => {
            setFaceIdEnrolled(session.faceIdEnrolled());
            setScreen({ name: "list" });
          }}
          onToast={showToast}
          onChangePassword={() => setScreen({ name: "changepw" })}
        />
      )}
      {screen.name === "changepw" && (
        <ChangePassword
          session={session}
          onDone={() => {
            showToast("Hasło zmienione");
            setScreen({ name: "settings" });
          }}
          onCancel={() => setScreen({ name: "settings" })}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  );
}
